import { Location } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PrequalificationModel } from 'src/app/models/prequalification.model';
import { RBACService } from '../../shared/rbac.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { PrequalificationService } from '../prequalification.service';
import { NetworkedAccountsSelectorComponent } from 'src/app/shared/networked-accounts-selector/networked-accounts-selector.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';

@Component({
  selector: 'app-prequalifications-view',
  templateUrl: './prequalifications-view.component.html',
  styleUrls: ['./prequalifications-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PrequalificationsViewComponent implements OnInit {
  prequal = new PrequalificationModel();

  selectedContractors: number[] = [];

  // RBAC permission flag: resolved once in ngOnInit().
  canEdit: boolean = false;

  constructor(
    public route: ActivatedRoute,
    private location: Location,
    private prequalService: PrequalificationService,
    private router: Router,
    public utils: UtilsService,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // 1. Check view permission: redirect if denied.
    const canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    if (!canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access pre-qualification templates.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 2. Resolve canEdit flag.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // 3. Load data.
    this.prequal.id = Number(this.route.snapshot.params['id']);
    this.prequalService.find(this.prequal.id).subscribe((response) => {
      this.prequal = response.data;
    });
  }

  delete() {
    this.prequalService
      .remove([this.prequal.id])
      .subscribe((prequal: PrequalificationModel) => {
        this.utils.showToast(`Deleted: "${prequal.name}".`);
        this.router.navigate([
          'prequalifications',
          {
            contractorId: this.prequal.contractor.id
          }
        ]);
      });
  }

  onCopyToContractors() {
    // Layer 2: Action guard, check edit permission before opening dialog.
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit pre-qualification templates.'
      );
      return;
    }

    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: this.selectedContractors,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.selectedContractors = results;
          if (this.selectedContractors.length > 0) {
            this.utils.showModal(
              'Confirmation',
              'Are you sure you would like to copy this pre-qualification to the selected ' +
                this.utils.getLangTerm('contractors.plural', 'Contractors') +
                '? For safety, existing ' +
                this.utils.getLangTerm('contractors.singular', 'Contractor') +
                ' Pre-Qualifications will not be replaced.',
              () => {
                this.api
                  .makeRequest(
                    'post',
                    'v1/copy-prequalification-to-contractors',
                    {
                      preQualificationId: this.prequal.id,
                      contractorIds: this.selectedContractors
                    }
                  )
                  .then((response) => {
                    this.utils.showToast(
                      'The Pre-Qualification was copied to the selected ' +
                        this.utils.getLangTerm(
                          'contractors.plural',
                          'Contractors'
                        ) +
                        '.'
                    );
                  });
              }
            );
          }
        }
      }
    );
  }

  back() {
    this.location.back();
  }
}
